import React, { useState, useEffect } from 'react';
import { personalInfo } from '../data/mockData';
import { COLORS, SPACING } from '../constants/designSystem';
import { Github, Linkedin, Twitter, Mail } from 'lucide-react';

const Footer = () => {
  const socialLinks = [
    { icon: Github, url: personalInfo.github, label: 'GitHub' },
    { icon: Linkedin, url: personalInfo.linkedin, label: 'LinkedIn' },
    { icon: Twitter, url: personalInfo.twitter, label: 'Twitter' },
    { icon: Mail, url: `mailto:${personalInfo.email}`, label: 'Email' },
  ];

  useEffect(() => {
    const katherineFont = new FontFace(
      'Katherine', 
      'url(/fonts/Katherine.otf) format("opentype")'
    );
    katherineFont.load().then((loadedFont) => {
      document.fonts.add(loadedFont);
    }).catch((err) => {
      console.error('Failed to load Katherine font:', err);
    });
  }, []);

  return (
    <footer
      style={{
        backgroundColor: COLORS.surface,
        borderTop: `1px solid ${COLORS.border}`,
        padding: `${SPACING.xl} ${SPACING.lg}`,
      }}
    >
      <div
        style={{
          maxWidth: '1400px',
          margin: '0 auto',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: SPACING.xl,
            marginBottom: SPACING.xl,
          }}
        >
          {/* About */}
          <div>
            <h3
              style={{
                fontSize: '28px',
                fontWeight: '600',
                color: COLORS.primary,
                marginBottom: SPACING.md,
                fontFamily: 'Katherine, sans-serif',
              }}
            >
              {personalInfo.nickname}
            </h3>
            <p
              style={{
                fontSize: '14px',
                lineHeight: '1.6',
                color: COLORS.text.secondary,
                marginBottom: SPACING.xs,
              }}
            >
              {personalInfo.title}
            </p>
            <p
              style={{
                fontSize: '13px',
                lineHeight: '1.6',
                color: COLORS.text.muted,
                marginBottom: SPACING.md,
              }}
            >
              Founder, <a 
                href={personalInfo.companyWebsite}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: COLORS.primary,
                  textDecoration: 'none',
                }}
                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
              >
                {personalInfo.companyName}
              </a>
            </p>
            <div style={{ display: 'flex', gap: SPACING.sm }}>
              {socialLinks.map(({ icon: Icon, url, label }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  style={{
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: COLORS.cardSurface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: '8px',
                    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${COLORS.primary}20`;
                    e.currentTarget.style.borderColor = COLORS.primary;
                    e.currentTarget.style.transform = 'translateY(-4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = COLORS.cardSurface;
                    e.currentTarget.style.borderColor = COLORS.border;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Icon size={18} color={COLORS.text.primary} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: COLORS.text.primary,
                marginBottom: SPACING.md,
              }}
            >
              Explore
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {['Projects', 'Awards', 'Teaching', 'About', 'Contact'].map((item) => (
                <li key={item} style={{ marginBottom: SPACING.xs }}>
                  <a
                    href={`/${item.toLowerCase()}`}
                    style={{
                      fontSize: '14px',
                      color: COLORS.text.secondary,
                      textDecoration: 'none',
                      transition: 'color 300ms',
                    }}
                    onMouseEnter={(e) => (e.target.style.color = COLORS.primary)}
                    onMouseLeave={(e) => (e.target.style.color = COLORS.text.secondary)}
                  >
                    {item}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4
              style={{
                fontSize: '16px',
                fontWeight: '600',
                color: COLORS.text.primary,
                marginBottom: SPACING.md,
              }}
            >
              Get In Touch
            </h4>
            <p style={{ fontSize: '14px', color: COLORS.text.secondary, marginBottom: SPACING.xs }}>
              {personalInfo.email}
            </p>
            <p style={{ fontSize: '14px', color: COLORS.text.secondary, marginBottom: SPACING.xs }}>
              {personalInfo.location}
            </p>
            <p
              style={{
                fontSize: '12px',
                color: COLORS.primary,
                marginTop: SPACING.md,
                padding: `${SPACING.xs} ${SPACING.sm}`,
                backgroundColor: `${COLORS.primary}15`,
                borderRadius: '6px',
                display: 'inline-block',
              }}
            >
              {personalInfo.status}
            </p>
          </div>
        </div>

        {/* Copyright */}
        <div
          style={{
            paddingTop: SPACING.lg,
            borderTop: `1px solid ${COLORS.border}`,
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: '14px',
              color: COLORS.text.secondary,
            }}
          >
            © {new Date().getFullYear()} {personalInfo.name}. All rights reserved. Built with passion and code.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
